import React, { useState } from "react";
import {
  inviteUserToCustomer,
  InviteToCustomerBody,
  SeatRole,
} from "../../../services/customerService";
import { Customer } from "../../../types/customer";
import { debug } from "../../../utils/debugLogger";

/** Sage focus ring — avoids browser/default blue outline on inputs (see `index.css` :focus-visible). */
const CONTROL_FOCUS =
  "focus:outline-none focus-visible:outline-none focus:border-theme-secondary focus:ring-2 focus:ring-theme-secondary/40 caret-theme-fg";

interface InviteUserSectionProps {
  idToken: string | null;
  selectedCustomer: Pick<Customer, "customer_id" | "business_name"> | null;
  onSuccess: () => void | Promise<void>;
  /** When true, renders as the right column card (no top divider; header with icon). */
  embedded?: boolean;
  /** Copy for the owner-facing card; defaults to the SuperAdmin wording. */
  title?: string;
  subtitle?: string;
}

/**
 * SuperAdmin invite on /users/all (SGL-390). Three separate choices, each said in
 * plain words: the Seat role (Owner / Team member), which allowance it spends
 * (this Brand / the Agency's operator seats), and — rarely — a Sagelyn staff
 * login. Brand Owners invite from the Team card instead (TeamSeatsCard).
 * The server enforces who may invite, who may grant Owner, and seat capacity.
 */
const ROLE_OPTIONS: { value: SeatRole; label: string; detail: string }[] = [
  {
    value: "MEMBER",
    label: "Team member",
    detail: "Chats with the agents, creates, approves and publishes. Cannot change billing, seats or connections.",
  },
  {
    value: "OWNER",
    label: "Owner",
    detail: "Everything a team member does, plus billing, inviting and removing people, connections and deleting the Brand.",
  },
];

const ALLOWANCE_OPTIONS: { value: "BRAND" | "AGENCY"; label: string; detail: string }[] = [
  {
    value: "BRAND",
    label: "This Brand only",
    detail: "Sees this Brand and nothing else. Uses one of this Brand's seats.",
  },
  {
    value: "AGENCY",
    label: "Agency operator",
    detail: "Works on every client Brand of this Agency. Uses one of the Agency's operator seats; refused on plans without them.",
  },
];
const LABEL_UPPER =
  "mb-1.5 block text-xs font-medium uppercase tracking-wide text-theme-fg-muted";

const InviteUserSection: React.FC<InviteUserSectionProps> = ({
  idToken,
  selectedCustomer,
  onSuccess,
  embedded = false,
  title = "Invite user to this business",
  subtitle = "Sends an invitation for the selected business profile. Requires a business profile above.",
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<SeatRole>("MEMBER");
  const [allowance, setAllowance] = useState<"BRAND" | "AGENCY">("BRAND");
  const [staffAccount, setStaffAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!idToken) {
      setMessage({ type: "error", text: "You are not signed in." });
      return;
    }
    if (!selectedCustomer) {
      setMessage({
        type: "error",
        text: "Select a business profile first.",
      });
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setMessage({ type: "error", text: "Email is required." });
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setMessage({ type: "error", text: "Name is required." });
      return;
    }

    const body: InviteToCustomerBody = {
      email: trimmedEmail,
      name: trimmedName,
      role,
      allowance,
      accountType: staffAccount ? "SuperAdmin" : "Customer",
    };

    try {
      setLoading(true);
      const data = (await inviteUserToCustomer(
        selectedCustomer.customer_id,
        body,
        idToken
      )) as { already_existed_in_cognito?: boolean };

      const existed = data?.already_existed_in_cognito === true;
      setMessage({
        type: "success",
        text: existed
          ? `Invitation sent. That email already had an account; they were linked to ${selectedCustomer.business_name || "this business"}.`
          : `Invitation sent. ${trimmedEmail} can join ${selectedCustomer.business_name || "this business"}.`,
      });
      setEmail("");
      setName("");
      setRole("MEMBER");
      setAllowance("BRAND");
      setStaffAccount(false);
      await onSuccess();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Invitation failed.";
      debug.error("InviteUserSection:", err);
      setMessage({ type: "error", text });
    } finally {
      setLoading(false);
    }
  };

  const disabled = !selectedCustomer || loading;

  const fieldClass = `h-10 w-full rounded-md border border-theme-border bg-theme-background px-3 text-sm text-theme-fg ${CONTROL_FOCUS} disabled:opacity-60`;

  const shellClass = embedded
    ? "flex h-full min-h-0 flex-col"
    : "mt-6 border-t border-theme-border pt-6";

  return (
    <div className={shellClass}>
      <div
        className={
          embedded ? "mb-4 flex items-start gap-2" : "mb-4 block"
        }
      >
        {embedded && (
          <span
            className="material-icons shrink-0 text-xl text-theme-fg-muted"
            aria-hidden
          >
            person_add
          </span>
        )}
        <div>
          <h3 className="text-base font-semibold text-theme-fg">{title}</h3>
          <p className="mt-1 text-sm text-theme-fg-muted">{subtitle}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="w-full space-y-4">
        <div>
          <label htmlFor="invite-email" className={LABEL_UPPER}>
            Email <span className="text-red-600">*</span>
          </label>
          <input
            id="invite-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled}
            placeholder="colleague@company.com"
            className={`${fieldClass} placeholder:text-slate-400`}
          />
        </div>

        <div>
          <label htmlFor="invite-name" className={LABEL_UPPER}>
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id="invite-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled}
            placeholder="Full name"
            className={`${fieldClass} placeholder:text-slate-400`}
          />
        </div>

        <fieldset disabled={disabled}>
          <legend className={LABEL_UPPER}>Role</legend>
          <div className="space-y-2">
            {ROLE_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 ${
                  role === o.value ? "border-theme-primary bg-theme-primary/5" : "border-theme-border"
                }`}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={o.value}
                  checked={role === o.value}
                  onChange={() => setRole(o.value)}
                  className="mt-0.5 text-theme-primary focus:ring-theme-primary"
                />
                <span>
                  <span className="block text-sm font-medium text-theme-fg">{o.label}</span>
                  <span className="block text-xs text-theme-fg-muted">{o.detail}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset disabled={disabled}>
          <legend className={LABEL_UPPER}>Access</legend>
          <div className="space-y-2">
            {ALLOWANCE_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 ${
                  allowance === o.value ? "border-theme-primary bg-theme-primary/5" : "border-theme-border"
                }`}
              >
                <input
                  type="radio"
                  name="invite-allowance"
                  value={o.value}
                  checked={allowance === o.value}
                  onChange={() => setAllowance(o.value)}
                  className="mt-0.5 text-theme-primary focus:ring-theme-primary"
                />
                <span>
                  <span className="block text-sm font-medium text-theme-fg">{o.label}</span>
                  <span className="block text-xs text-theme-fg-muted">{o.detail}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-theme-fg">
          <input
            type="checkbox"
            checked={staffAccount}
            onChange={(e) => setStaffAccount(e.target.checked)}
            disabled={disabled}
            className="mt-0.5 rounded text-red-600 focus:ring-red-600"
          />
          <span>
            <span className="block font-medium">Sagelyn staff account</span>
            <span className="block text-xs text-theme-fg-muted">
              Gives full admin access to every Brand. Only for Sagelyn employees.
            </span>
          </span>
        </label>

        {message && (
          <div
            role="alert"
            className={`rounded-md border px-3 py-2 text-sm ${
              message.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className={embedded ? "flex justify-end pt-1" : ""}>
          <button
            type="submit"
            disabled={disabled || !email.trim() || !name.trim()}
            className={`rounded-md bg-theme-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-secondary/55 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-background disabled:cursor-not-allowed disabled:opacity-50 ${embedded ? "" : "w-full sm:w-auto"}`}
          >
            {loading ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InviteUserSection;
